import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@mui/styles";
import {
  Box,
  Grid,
  TextField,
  Button,
  Paper,
  Avatar,
  AvatarGroup,
  IconButton,
  Chip,
  Typography,
  Collapse,
  AppBar,
  Toolbar,
  InputAdornment,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

//... Icons
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  SportsEsports as SportsEsportsIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";

//... Context & helpers
import AppDataContext from "../../common/appContext";
import Helper from "../../common/Helper";
import RouteConstants from "../../common/RouteConstants";

//... Services & Components
import { Game5On5Services } from "../../services/game5On5.Service";
import { PlayerProfileService } from "../../services/playerProfile.Service";
import { baseUrl, Video_baseUrl } from "../../services/AzureStorage";

//... Shared Components
import EmptyUI from "../../sharedComponents/emptyStateUIContainers/EmptyUI";
import SlidingPaneComponent from "../../sharedComponents/SlidingPane/SlidingPane";
import AlertSnackbar from "../../sharedComponents/Snackbar/AlertSnackbar";
import Loader from "../../sharedComponents/loader/loader";
import ConfirmationDialog from "../../sharedComponents/dialog/ConfirmationDialog";
import TitleTooltip from "../../sharedComponents/Tooltip/TitleTooltip";

//... Local Components
import GameFormCard from "./GameFormCard";

const useStyles = makeStyles(() => ({
  page: {
    padding: "24px",
    minHeight: "100vh",
    background: "linear-gradient(180deg, #f5f7fb 0%, #eef2ff 100%)",

    "@media (max-width: 600px)": {
      padding: "12px",
    },
  },
  listCard: {
    background: "#fff",
    borderRadius: 14,
    padding: 14,
    marginBottom: 10,
    display: "flex",
    flexDirection: "column",
    border: "1px solid #eef0f6",
    transition: "all 0.25s ease",
    position: "relative",

    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: "0 12px 28px rgba(79,70,229,0.12)",
      border: "1px solid rgba(79,70,229,0.2)",
    },

    "@media (max-width: 600px)": {
      padding: 12,
    },
  },
  leftBorder: {
    width: 6,
    height: 50,
    background: "linear-gradient(180deg, #4f46e5, #22c55e)",
    borderRadius: 10,
    marginRight: 12,
    boxShadow: "0 4px 10px rgba(79,70,229,0.2)",

    "@media (max-width: 600px)": {
      height: 40,
      marginRight: 8,
    },
  },
  gameTitle: {
    fontWeight: 700,
    fontSize: 15,

    "@media (max-width: 600px)": {
      fontSize: 14,
    },
  },
  created: {
    fontSize: 12,
    color: "#6b7280",
    wordBreak: "break-word",
  },
}));

const renderTeam = (label, teamName, players, color, viewPlayerProfile) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 1.5,
    }}
  >
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 700,
        color: "#64748b",
        textTransform: "uppercase",
        letterSpacing: 1,
        minWidth: 70,
      }}
    >
      {label}
    </Typography>

    <Typography
      sx={{
        fontSize: 14,
        fontWeight: 800,
        color,
        minWidth: 120,
      }}
    >
      {teamName}
    </Typography>

    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, flex: 1 }}>
      {players?.length ? (
        players.map((p) => (
          <Chip
            key={p.id}
            label={p.name}
            clickable
            onClick={() => viewPlayerProfile(p.id)}
            avatar={
              <Avatar src={p.headShotUrl || ""} sx={{ bgcolor: color }}>
                {Helper.GetNameInitials(p.name)}
              </Avatar>
            }
            sx={{
              background: "#fff",
              border: "none",
              fontSize: 12,
              cursor: "pointer",
              textDecoration: "underline",
              color: "#405ea6",
              fontWeight: 800,
              border: "1px solid #dbeafe",
              "&:hover": {
                background: "#f1f5f9",
                transform: "scale(1.03)",
                transition: "0.2s",
              },
              "& .MuiChip-avatar": {
                fontWeight: 600,
                color: "#fff",
              },
            }}
          />
        ))
      ) : (
        <Typography sx={{ fontSize: 12, color: "#64748b" }}>
          No players tagged
        </Typography>
      )}
    </Box>
  </Box>
);

export default function Manage5On5GamesContainer() {
  const classes = useStyles();
  const navigate = useNavigate();

  const { user, selectedEventId } = useContext(AppDataContext);

  //... States
  const [games, setGames] = useState([]);
  const [playersList, setPlayersList] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [deleteGame, setDeleteGame] = useState(null);
  const [expandedGameId, setExpandedGameId] = useState(null);
  const [searchText, setSearchText] = useState("");

  //... Loading & Dialog States
  const [loading, setLoading] = useState(false);
  const [openDeleteEventDialog, setOpenDeleteEventDialog] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const expandedRef = useRef(null);

  //... Form State
  const initialForm = {
    name: "",
    url: "",
    team1Name: "",
    team2Name: "",
    team1Players: [],
    team2Players: [],
  };

  const [form, setForm] = useState(initialForm);

  //... Alert State
  const [alert, setAlert] = useState({
    open: false,
    type: "warning",
    message: "",
  });

  const isEditMode = Boolean(selectedGame);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (expandedRef.current && !expandedRef.current.contains(event.target)) {
        setExpandedGameId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!selectedEventId || !user?.loginID) return;

    resetGameUIState();

    const init = async () => {
      setLoading(true);
      const players = await fetchPlayers();
      await fetchGames(players);
      setLoading(false);
    };

    init();
  }, [selectedEventId, user?.loginID]);

  //... Utility Functions
  const validate = () => {
    if (!form.name?.trim()) {
      showAlert("warning", "Game name required");
      return false;
    }

    if (!form.url?.trim()) {
      showAlert("warning", "URL required");
      return false;
    }

    if (!Helper.isValidVideoInput(form.url)) {
      showAlert("warning", "Enter a valid video URL or file name");
      return false;
    }

    if (!form.team1Name?.trim()) {
      showAlert("warning", "Team 1 name required");
      return false;
    }

    if (!form.team2Name?.trim()) {
      showAlert("warning", "Team 2 name required");
      return false;
    }

    if (!form.team1Players?.length) {
      showAlert("warning", "Select Team 1 players");
      return false;
    }

    if (!form.team2Players?.length) {
      showAlert("warning", "Select Team 2 players");
      return false;
    }

    return true;
  };

  const showAlert = (type, message) => {
    setAlert({
      open: true,
      type,
      message,
    });
  };

  const buildGamePayload = () => {
    const toIdString = (players = []) => players.map((p) => p.id).join(",");

    const url = form.url.trim();
    //const finalUrl = /^https?:\/\//i.test(url) ? url : Video_baseUrl(url);

    return {
      gameVideosId: isEditMode ? selectedGame?.gameVideosId : 0,
      eventId: selectedEventId,
      stationId: selectedGame?.stationId || 701,
      gameName: form.name,
      url: url,
      team1Name: form.team1Name,
      team1PlayerIds: toIdString(form.team1Players),
      team2Name: form.team2Name,
      team2PlayerIds: toIdString(form.team2Players),
      userBy: user?.loginID,
    };
  };

  //... Handlers
  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setLoading(true);
      const payload = buildGamePayload();
      await Game5On5Services.addUpdate5On5Game(payload);

      showAlert(
        "success",
        isEditMode ? "The game has been updated." : "The game has been created."
      );

      setSelectedGame(null);
      setForm(initialForm);
      await fetchGames();
    } catch {
      showAlert("error", "Save failed");
    } finally {
      setLoading(false);
      handleCancel();
    }
  };

  const handleEdit = (game) => {
    setSelectedGame(game);

    setForm({
      name: game.gameName || "",
      url: game.url || "",
      team1Name: game.team1Name || "",
      team2Name: game.team2Name || "",
      team1Players: game.team1PlayersList || [],
      team2Players: game.team2PlayersList || [],
    });

    setIsPanelOpen(true);
  };

  const handleOpenDeleteDialog = (game) => {
    setDeleteGame(game);
    setOpenDeleteEventDialog(true);
  };

  const onDeleteEventAgree = async () => {
    try {
      setLoading(true);
      setOpenDeleteEventDialog(false);
      const res = await Game5On5Services.delete5On5Game(
        deleteGame.gameVideosId,
        user.loginID
      );

      if (res?.data?.data) {
        showAlert("success", `"${deleteGame?.gameName || ""}" deleted`);
        await fetchGames();
      }
    } catch {
      showAlert("error", "Delete failed");
    } finally {
      setLoading(false);
      setDeleteGame(null);
    }
  };

  const handleCancel = () => {
    setSelectedGame(null);
    setForm(initialForm);
    setIsPanelOpen(false);
  };

  const onCreateNew = () => {
    setSelectedGame(null);
    setForm(initialForm);
    setIsPanelOpen(true);
  };

  const handleToggleExpand = (id) => {
    setExpandedGameId((prev) => (prev === id ? null : id));
  };

  const viewPlayerProfile = (playerId) => {
    navigate(
      `/${selectedEventId}${RouteConstants.VIEW_PLAYER_PROFILE}/${playerId}`
    );
  };

  const resetGameUIState = () => {
    setSelectedGame(null);
    setForm(initialForm);
    setExpandedGameId(null);
    setIsPanelOpen(false);
    setSearchText("");
  };

  //... API Calls
  const fetchPlayers = async () => {
    try {
      const res = await PlayerProfileService.getPlayerListByEventIdYear(
        selectedEventId
      );

      const players = (res?.data || []).map((p) => ({
        id: p.playerID,
        name: p.playerName,
        headShotUrl: p.headshotURL ? baseUrl(p.headshotURL) : "",
      }));

      setPlayersList(players);
      return players;
    } catch {
      setPlayersList([]);
      return [];
    }
  };

  const fetchGames = async (players = playersList) => {
    try {
      const res = await Game5On5Services.get5On5GameVideos(selectedEventId);
      const map = new Map(players.map((p) => [Number(p.id), p]));
      const gamesData = res?.data?.data || [];

      const formatted = gamesData.map((g) => ({
        ...g,
        team1PlayersList: (g.team1PlayerIds || "")
          .split(",")
          .filter(Boolean)
          .map((id) => map.get(Number(id)))
          .filter(Boolean),

        team2PlayersList: (g.team2PlayerIds || "")
          .split(",")
          .filter(Boolean)
          .map((id) => map.get(Number(id)))
          .filter(Boolean),
      }));

      setGames(formatted);
    } catch {
      setGames([]);
    }
  };

  const filteredGames = useMemo(() => {
    if (!searchText.trim()) return games;

    return games.filter((g) =>
      (g.gameName || "").toLowerCase().includes(searchText.toLowerCase())
    );
  }, [games, searchText]);

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid #e5e7eb",
          mb: 2,
        }}
      >
        <Toolbar
          sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}
        >
          <Box>
            <Typography
              sx={{ fontSize: 18, fontWeight: 800, color: "#111827" }}
            >
              5 On 5 Games Management
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
              Manage games, teams & player mappings
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <TextField
              size="small"
              placeholder="Search by game..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{
                width: 220,
                background: "white",
                borderRadius: 2,
              }}
              InputProps={{
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchText("")}
                      edge="end"
                    >
                      <ClearIcon
                        fontSize="small"
                        sx={{
                          color: "#ef4444",
                          transition: "0.2s",
                          "&:hover": {
                            color: "#dc2626",
                            transform: "scale(1.1)",
                          },
                        }}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={onCreateNew}
              sx={{
                textTransform: "none",
                borderRadius: 3,
                px: 2,
                background: "linear-gradient(135deg, #1d428a, #2c5cc5)",
              }}
            >
              Create New Game
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Box className={classes.page}>
        <Loader isLoading={loading} />

        {filteredGames.length === 0 ? (
          <Grid container p={1}>
            <Grid item lg={12}>
              <EmptyUI
                heading={
                  searchText.trim()
                    ? "No matching games found."
                    : "No games available."
                }
                subHeading={
                  searchText.trim()
                    ? "Try adjusting your search or clear the filter."
                    : "Create your first game to get started."
                }
                icon={<SportsEsportsIcon />}
                styles={{ minHeight: "50vh" }}
                iconStyles={{
                  fontSize: "clamp(10rem, 1vw + 8rem, 10rem)",
                  color: "#8c9dc4",
                }}
              />
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={2}>
            {filteredGames.map((game) => {
              const uniquePlayers = Array.from(
                new Map(
                  [
                    ...(game.team1PlayersList || []),
                    ...(game.team2PlayersList || []),
                  ].map((p) => [p.id, p])
                ).values()
              );

              return (
                <Grid item xs={12} key={game.gameVideosId}>
                  <Paper
                    className={classes.listCard}
                    ref={
                      expandedGameId === game.gameVideosId ? expandedRef : null
                    }
                  >
                    <Grid container alignItems="center" spacing={2}>
                      {/* LEFT SIDE */}
                      <Grid item xs={12} sm={7}>
                        {/* FIXED ROW (border + content aligned properly) */}
                        <Grid
                          container
                          alignItems="center"
                          spacing={1}
                          wrap="nowrap"
                        >
                          {/* LEFT BORDER FIX (no Grid item wrapper) */}
                          <Box
                            sx={{
                              width: 6,
                              minHeight: 50,
                              borderRadius: 2,
                              background:
                                "linear-gradient(180deg, #4f46e5, #22c55e)",
                              flexShrink: 0,
                              alignSelf: "stretch",
                            }}
                          />

                          {/* CONTENT */}
                          <Grid item xs>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 16,
                                  fontWeight: 700,
                                  color: "#0f172a",
                                }}
                              >
                                {game.gameName}
                              </Typography>

                              <Typography sx={{ color: "#94a3b8" }}>
                                │
                              </Typography>

                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: "#2563eb",
                                }}
                              >
                                {game.team1Name}
                              </Typography>

                              <Typography sx={{ color: "#94a3b8" }}>
                                vs
                              </Typography>

                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: "#ef4444",
                                }}
                              >
                                {game.team2Name}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                mt: 0.8,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 2,
                                  background:
                                    "linear-gradient(135deg, #e0f2fe, #f0f9ff)",
                                  border: "1px solid #bae6fd",
                                  maxWidth: "70%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  fontSize: 12,
                                  color: "#0369a1",
                                }}
                              >
                                {game.url}
                              </Box>

                              <IconButton
                                size="small"
                                onClick={() => {
                                  navigator.clipboard.writeText(game.url);
                                  showAlert("success", "URL copied");
                                }}
                                sx={{
                                  background: "#f1f5f9",
                                  "&:hover": { background: "#e2e8f0" },
                                }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* RIGHT SIDE */}
                      <Grid
                        item
                        xs={12}
                        sm={5}
                        container
                        justifyContent={{
                          xs: "flex-start",
                          sm: "flex-end",
                        }}
                        alignItems="center"
                        spacing={1}
                      >
                        <Grid
                          item
                          sx={{
                            display: {
                              xs: "none",
                              sm: "block",
                            },
                          }}
                        >
                          <AvatarGroup max={6}>
                            {uniquePlayers.map((p) => (
                              <Avatar
                                key={p.id}
                                src={p.headShotUrl || ""}
                                sx={{ bgcolor: "#1d428a" }}
                              >
                                {!p.headShotUrl &&
                                  Helper.GetNameInitials(p.name)}
                              </Avatar>
                            ))}
                          </AvatarGroup>
                        </Grid>

                        <Grid item>
                          <IconButton onClick={() => handleEdit(game)}>
                            <TitleTooltip title="Edit Game" placement="top">
                              <EditIcon />
                            </TitleTooltip>
                          </IconButton>
                        </Grid>

                        <Grid item>
                          <IconButton
                            onClick={() => handleOpenDeleteDialog(game)}
                            sx={{
                              color: "error.main",
                              transition: "0.2s",
                              "&:hover": {
                                backgroundColor: "rgba(211, 47, 47, 0.1)",
                                color: "error.dark",
                              },
                            }}
                          >
                            <TitleTooltip title="Delete Game" placement="top">
                              <DeleteIcon />
                            </TitleTooltip>
                          </IconButton>
                        </Grid>

                        <Grid item>
                          <IconButton
                            onClick={() =>
                              handleToggleExpand(game.gameVideosId)
                            }
                            sx={{
                              transition: "0.2s",
                              "&:hover": {
                                transform: "scale(1.1)",
                              },
                            }}
                          >
                            <TitleTooltip
                              title={
                                expandedGameId === game.gameVideosId
                                  ? "Hide Tagged Players"
                                  : "View Tagged Players"
                              }
                              placement="top"
                            >
                              {expandedGameId === game.gameVideosId ? (
                                <ExpandMoreIcon color="primary" />
                              ) : (
                                <VisibilityIcon color="primary" />
                              )}
                            </TitleTooltip>
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* EXPAND SECTION */}
                    <Collapse
                      in={expandedGameId === game.gameVideosId}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box
                        sx={{
                          mt: 2,
                          borderRadius: 3,
                          border: "1px solid #e5e7eb",
                          background: "#f8fafc",
                          p: 2,
                          display: "grid",
                          gap: 2,
                        }}
                      >
                        {renderTeam(
                          "Team 1",
                          game.team1Name,
                          game.team1PlayersList,
                          "#1d428a",
                          viewPlayerProfile
                        )}

                        {renderTeam(
                          "Team 2",
                          game.team2Name,
                          game.team2PlayersList,
                          "#dc2626",
                          viewPlayerProfile
                        )}
                      </Box>
                    </Collapse>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Snackbar */}
        <AlertSnackbar
          open={alert.open}
          message={alert.message}
          type={alert.type}
          onClose={() => setAlert({ ...alert, open: false })}
        />

        {/* Delete Dialog */}
        <ConfirmationDialog
          open={openDeleteEventDialog}
          message="Are you sure you want to delete this game?"
          handleClose={() => setOpenDeleteEventDialog(false)}
          handleAgree={onDeleteEventAgree}
          handleDisAgree={() => setOpenDeleteEventDialog(false)}
          maxWidth="sm"
        />

        {/* Form Panel */}
        <SlidingPaneComponent
          isOpen={isPanelOpen}
          title={!isEditMode ? "Create Game" : "Update Game"}
          onRequestClose={handleCancel}
        >
          <GameFormCard
            classes={classes}
            isEditMode={isEditMode}
            form={form}
            setForm={setForm}
            playersList={playersList}
            handleSubmit={handleSubmit}
            handleCancel={handleCancel}
          />
        </SlidingPaneComponent>
      </Box>
    </>
  );
}
